/*
  Gild product page scripts. <gild-scroll-progress> is at the end of the file.

  <gild-product-gallery>, the "Two-up scroll grid" product gallery (snippets/gild-product-gallery.liquid).
  Client request 2026-10-15: mirror the Babyboo PDP gallery.

  - Item offsets are read against the viewport, which is the items' offsetParent (position: relative in the CSS).
  - Marks the desktop gallery .is-pinned once it has reached its pin and is mostly on screen; only then do its images scroll on their own.
  - Plays a video slide while it is on screen (with the section's video autoplay on) and pauses it when it leaves.
  - Phones and tablets: marks the thumbnail of the image in view, and a thumbnail tap scrolls the gallery to its image.
  - Builds the desktop dot rail (one dot per row, of two images or, on "Single column scroll", one) and the phone/tablet progress bar (one segment per image)
    from the images that are visible, and keeps the active one in step with the scroll position.
  - Scrolls the gallery (never the page) to the variant's image on variant change, and applies Focal's "#" alt-tag
    variant image sets the same way Focal's <product-media> does.
  - Opens Focal's PhotoSwipe zoom (<product-image-zoom>) on the image that was clicked.
*/
(() => {
  const GRID_QUERY = '(min-width: 1000px)';

  class GildProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.gild-gallery__viewport');
      this.rail = this.querySelector('.gild-gallery__rail');
      this.progress = this.querySelector('.gild-gallery__progress');
      this.thumbnails = this.querySelector('.gild-gallery__thumbnails');
      this.zoom = this.querySelector('product-image-zoom');
      this.gridQuery = window.matchMedia(GRID_QUERY);
      this.abortController = new AbortController();

      const signal = this.abortController.signal;

      this.viewport.addEventListener('scroll', () => { this.update(); this.updatePinned(); }, { passive: true, signal });
      window.addEventListener('scroll', () => this.updatePinned(), { passive: true, signal });
      window.addEventListener('resize', () => { this.update(); this.updatePinned(); }, { signal });
      this.gridQuery.addEventListener('change', () => { this.build(); this.updatePinned(); }, { signal });

      this.thumbnails?.addEventListener('click', (event) => {
        const thumbnail = event.target.closest('[data-media-id]');

        if (thumbnail) {
          this.scrollToItem(this.querySelector(`.gild-gallery__item[data-media-id="${thumbnail.getAttribute('data-media-id')}"]`));
        }
      }, { signal });

      if (this.zoom) {
        this.viewport.addEventListener('click', (event) => this.onImageClick(event), { signal });
      }

      document.getElementById(this.getAttribute('form-id'))?.addEventListener('variant:change', (event) => this.onVariantChange(event), { signal });

      // Focal's carousel starts a video when its slide is selected; here a video plays when at least half of it is
      // on screen (if the section's video autoplay is on) and pauses when it leaves. Without this a video slide
      // stayed an empty panel, as autoplay leaves out the poster.
      const players = this.querySelectorAll('.gild-gallery__item native-video, .gild-gallery__item external-video');

      if (players.length > 0) {
        this.videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.hasAttribute('autoplay-video') && entry.target.play();
            } else {
              entry.target.pause();
            }
          });
        }, { threshold: .5 });

        players.forEach((player) => this.videoObserver.observe(player));
      }

      // The buy box grows after load (app widgets, accordions), which moves the pin: re-check whenever the row resizes
      this.resizeObserver = new ResizeObserver(() => this.updatePinned());
      this.resizeObserver.observe(this.parentElement);

      this.build();
      this.updatePinned();

      const selected = this.querySelector('.gild-gallery__item.is-selected:not(.is-filtered)');

      if (selected && selected !== this.items[0]) {
        this.scrollToItem(selected, 'instant');
      }
    }

    disconnectedCallback() {
      this.abortController.abort();
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
    }

    get items() {
      return Array.from(this.querySelectorAll('.gild-gallery__item:not(.is-filtered)'));
    }

    get isGrid() {
      return this.gridQuery.matches;
    }

    get imagesPerRow() {
      return this.classList.contains('gild-gallery--single') ? 1 : 2;
    }

    build() {
      const items = this.items, imagesPerRow = this.imagesPerRow;

      // A lone last image spans both columns, as on Babyboo
      this.querySelectorAll('.gild-gallery__item').forEach((item) => item.classList.remove('gild-gallery__item--full'));

      if (imagesPerRow === 2 && items.length % 2 === 1 && items.length > 1) {
        items[items.length - 1].classList.add('gild-gallery__item--full');
      }

      const rows = Math.ceil(items.length / imagesPerRow);

      this.rail.replaceChildren(...(rows > 1 ? Array.from({ length: rows }, (_, index) => this.createControl('gild-gallery__dot', () => this.scrollToItem(this.items[index * imagesPerRow]))) : []));
      this.progress.replaceChildren(...(items.length > 1 ? items.map((item) => this.createControl('gild-gallery__segment', () => this.scrollToItem(item))) : []));

      this.update();
    }

    createControl(className, onClick) {
      const button = document.createElement('button');

      button.type = 'button';
      button.tabIndex = -1;
      button.className = className;
      button.addEventListener('click', onClick);

      return button;
    }

    update() {
      const items = this.items, viewport = this.viewport;

      if (items.length === 0) {
        return;
      }

      if (this.isGrid) {
        const atEnd = viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 1;
        const imagesPerRow = this.imagesPerRow;
        let row = 0;

        for (let index = 0; index < items.length; index += imagesPerRow) {
          if (items[index].offsetTop <= viewport.scrollTop + items[index].offsetHeight / 2) {
            row = index / imagesPerRow;
          }
        }

        this.setActive(this.rail, atEnd ? this.rail.children.length - 1 : row);
      } else {
        const scrollLeft = Math.abs(viewport.scrollLeft), atEnd = scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 1;
        const index = Math.round(scrollLeft / (items[0].offsetWidth || 1));

        const activeIndex = Math.min(atEnd ? items.length - 1 : index, items.length - 1);

        this.setActive(this.progress, activeIndex);
        this.setActiveThumbnail(items[activeIndex]);
      }
    }

    // Phones and tablets: mark the thumbnail of the image in view, and bring it into view within the strip
    // (the strip scrolls, never the page)
    setActiveThumbnail(item) {
      if (!this.thumbnails || !item) {
        return;
      }

      const mediaId = item.getAttribute('data-media-id');

      if (mediaId === this.activeThumbnailId) {
        return;
      }

      this.activeThumbnailId = mediaId;

      const thumbnail = this.thumbnails.querySelector(`[data-media-id="${mediaId}"]`);

      this.thumbnails.querySelectorAll('[aria-current]').forEach((current) => current.removeAttribute('aria-current'));

      if (thumbnail) {
        thumbnail.setAttribute('aria-current', 'true');
        this.thumbnails.scrollTo({ left: thumbnail.offsetLeft - (this.thumbnails.clientWidth - thumbnail.offsetWidth) / 2, behavior: 'smooth' });
      }
    }

    // Desktop: .is-pinned lets the images scroll on their own (see the CSS). It is set once the gallery has
    // reached its pin under the header (its top at or above the sticky offset) while at least 75% of it is on
    // screen. Before that a wheel over the gallery moves the page up to it; once it has mostly left the screen
    // the page scrolls again. A band rather than the exact pin, because the pin can last only a few pixels (a
    // short buy box or a tall screen) and one wheel step would jump past it, leaving the images unreachable.
    updatePinned() {
      if (!this.isGrid) {
        this.classList.remove('is-pinned');
        return;
      }

      const stickyTop = parseFloat(getComputedStyle(this).top) || 0, rect = this.getBoundingClientRect();
      const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, stickyTop);

      this.classList.toggle('is-pinned', rect.top <= stickyTop + 2 && visibleHeight >= rect.height * .75);
    }

    setActive(container, activeIndex) {
      Array.from(container.children).forEach((child, index) => child.classList.toggle('is-active', index === activeIndex));
    }

    scrollToItem(item, behavior = 'smooth') {
      if (!item) {
        return;
      }

      if (this.isGrid) {
        this.viewport.scrollTo({ top: item.offsetTop, behavior });
      } else {
        this.viewport.scrollTo({ left: item.offsetLeft, behavior });
      }
    }

    onImageClick(event) {
      const item = event.target.closest('.gild-gallery__item[data-media-type="image"]');

      if (!item) {
        return;
      }

      this.querySelectorAll('.gild-gallery__item.is-selected').forEach((selected) => selected.classList.remove('is-selected'));
      item.classList.add('is-selected');
      this.zoom.open = true;
    }

    onVariantChange(event) {
      const variant = event.detail.variant;

      if (!variant) {
        return;
      }

      // Focal's "#" alt-tag variant image sets: an image tagged "#Color_Olive" only shows for the Olive variant
      const optionNames = JSON.parse(this.querySelector('[data-gild-gallery-options]')?.textContent || '[]').map((name) => name.toLowerCase());
      let filterChanged = false;

      this.querySelectorAll('.gild-gallery__item').forEach((item) => {
        const alt = item.getAttribute('data-media-alt') || '';
        const isFeatured = variant['featured_media'] && parseInt(item.getAttribute('data-media-id')) === variant['featured_media']['id'];

        if (!alt.includes('#')) {
          return;
        }

        const [groupName, groupValue = ''] = alt.split('#').pop().split('_');
        const optionIndex = optionNames.indexOf(groupName.toLowerCase());
        const filtered = !isFeatured && optionIndex !== -1 && variant['options'][optionIndex].toLowerCase() !== groupValue.trim().toLowerCase();

        if (item.classList.contains('is-filtered') !== filtered) {
          item.classList.toggle('is-filtered', filtered);
          this.thumbnails?.querySelector(`[data-media-id="${item.getAttribute('data-media-id')}"]`)?.classList.toggle('is-filtered', filtered);
          filterChanged = true;
        }
      });

      if (filterChanged) {
        this.build();
      }

      if (variant['featured_media']) {
        const item = this.querySelector(`.gild-gallery__item[data-media-id="${variant['featured_media']['id']}"]`);

        this.querySelectorAll('.gild-gallery__item.is-selected').forEach((selected) => selected.classList.remove('is-selected'));
        item?.classList.add('is-selected');
        this.scrollToItem(item);
      } else if (filterChanged) {
        this.scrollToItem(this.items[0], 'instant');
      }
    }
  }

  /*
    <gild-scroll-progress for="scroller-id">: the 2px segmented bar under a sideways scroller ("Wear it with").
    One segment per snap position (a card at a time), the current one in ink; a segment scrolls to its card.
  */
  class GildScrollProgress extends HTMLElement {
    connectedCallback() {
      this.scroller = document.getElementById(this.getAttribute('for'));

      if (!this.scroller) {
        return;
      }

      this.abortController = new AbortController();

      const signal = this.abortController.signal;

      this.scroller.addEventListener('scroll', () => this.update(), { passive: true, signal });
      window.addEventListener('resize', () => this.build(), { signal });

      this.build();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    get items() {
      return Array.from(this.scroller.children);
    }

    build() {
      const items = this.items, perView = Math.max(1, Math.round(this.scroller.clientWidth / (items[0]?.offsetWidth || 1)));
      const positions = Math.max(0, items.length - perView + 1);

      this.replaceChildren(...(positions > 1 ? Array.from({ length: positions }, (_, index) => {
        const button = document.createElement('button');

        button.type = 'button';
        button.tabIndex = -1;
        button.className = 'gild-gallery__segment';
        button.addEventListener('click', () => this.scroller.scrollTo({ left: items[index].offsetLeft - items[0].offsetLeft, behavior: 'smooth' }));

        return button;
      }) : []));

      this.update();
    }

    update() {
      const scroller = this.scroller, items = this.items;

      if (items.length < 2) {
        return;
      }

      const step = items[1].offsetLeft - items[0].offsetLeft || 1, scrollLeft = Math.abs(scroller.scrollLeft);
      const atEnd = scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
      const activeIndex = atEnd ? this.children.length - 1 : Math.round(scrollLeft / step);

      Array.from(this.children).forEach((child, index) => child.classList.toggle('is-active', index === activeIndex));
    }
  }

  /**
   * <gild-complete-look>: fills the "Complete the Look" row (sections/product-recommendations.liquid).
   * The section has already rendered the complementary cards ahead of this element. This fetches the
   * related-products render of the same section, skips products already on the page (the product itself
   * and its whole complementary list), and appends related cards until the row holds recommendations-count.
   * Reveals the section once it has at least one card.
   */
  class GildCompleteLook extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.loaded) return;
      this.dataset.loaded = 'true';

      const wrapper = this.closest('[data-gild-complete-look-wrapper]');
      const count = parseInt(this.getAttribute('recommendations-count') || 4, 10);
      const needed = count - parseInt(this.getAttribute('lead-count') || 0, 10);
      const exclude = new Set((this.getAttribute('exclude') || '').split(',').filter(Boolean));

      if (needed > 0) {
        try {
          const url = `${window.themeVariables.routes.productRecommendationsUrl}?product_id=${this.getAttribute('product-id')}&limit=10&section_id=${this.getAttribute('section-id')}&intent=related`;
          const html = await (await fetch(url)).text();
          const source = new DOMParser().parseFromString(html, 'text/html');
          const cards = Array.from(source.querySelectorAll('.gild-complete-look__card'))
            .filter((card) => !exclude.has(card.dataset.productId))
            .slice(0, needed);

          cards.forEach((card) => this.appendChild(document.importNode(card, true)));
        } catch (error) {
          // Related products are only a top-up; the complementary cards stand on their own
        }
      }

      const inner = this.parentElement;
      if (wrapper && inner && inner.querySelector('product-item')) {
        wrapper.hidden = false;
      }
    }
  }

  // Both snippets load this file, so define each element once
  if (!window.customElements.get('gild-product-gallery')) {
    window.customElements.define('gild-product-gallery', GildProductGallery);
  }

  if (!window.customElements.get('gild-complete-look')) {
    window.customElements.define('gild-complete-look', GildCompleteLook);
  }

  if (!window.customElements.get('gild-scroll-progress')) {
    window.customElements.define('gild-scroll-progress', GildScrollProgress);
  }
})();
